import { useState, useEffect } from 'react'
import { useProject } from '@/contexts/project-context'
import { createClerkSupabaseClient } from '@/lib/supabase'
import { useGetToken } from '@/lib/hooks/use-auth'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { AlertCircle, Clock, Calendar, TrendingDown } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

interface OptionGrant {
  id: string
  user_id: string
  user_name: string
  shares_offered: number
  strike_price: number
  option_type: string
  grant_date: string
  vesting_start_date: string
  expiration_date: string
  exercise_window_months: number
  vested_shares: number
  exercised_shares: number
  status: string
  days_until_expiration: number
}

export default function OptionTrackingPage() {
  const getToken = useGetToken()
  const [options, setOptions] = useState<OptionGrant[]>([])
  const [loading, setLoading] = useState(true)
  const [expiringCount, setExpiringCount] = useState(0)
  const [vestedUnexercised, setVestedUnexercised] = useState(0)
  const { currentProject } = useProject()

  useEffect(() => {
    if (currentProject) {
      fetchOptions()
    }
  }, [currentProject])

  const fetchOptions = async () => {
    setLoading(true)
    
    // Fetch accepted option offers
    if (!currentProject?.id) {
      setLoading(false)
      return
    }

    // Create authenticated Supabase client with Clerk token
      const authenticatedClient = createClerkSupabaseClient(getToken)
      
      const { data: optionData, error } = await authenticatedClient
      .from('equity_offers')
      .select(`
        id,
        user_id,
        shares_offered,
        strike_price,
        option_type,
        exercise_window_months,
        created_at,
        status,
        users!equity_offers_user_id_fkey (
          id,
          email,
          first_name,
          last_name
        ),
        vesting_schedules!equity_offers_vesting_schedule_id_fkey (
          duration_months,
          cliff_months
        )
      `)
      .eq('project_id', currentProject.id)
      .eq('equity_type', 'options') // Options only
      .eq('status', 'accepted')
      .order('created_at', { ascending: false })

    if (optionData && !error) {
      // Calculate expiration dates and vesting status
      const processedOptions = optionData.map((offer: any) => {
        const grantDate = new Date(offer.created_at)
        const vestingSchedule = offer.vesting_schedules
        
        // Calculate expiration date (typically 10 years from grant)
        const expirationDate = new Date(grantDate)
        expirationDate.setFullYear(expirationDate.getFullYear() + 10)
        
        // Calculate days until expiration
        const today = new Date()
        const daysUntilExpiration = Math.floor(
          (expirationDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
        )
        
        // Calculate vested shares (simplified - would need actual vesting calculations)
        const monthsSinceGrant = Math.floor(
          (today.getTime() - grantDate.getTime()) / (1000 * 60 * 60 * 24 * 30)
        )
        
        let vestedShares = 0
        if (vestingSchedule && monthsSinceGrant >= vestingSchedule.cliff_months) {
          const vestingMonths = Math.min(monthsSinceGrant, vestingSchedule.duration_months)
          vestedShares = Math.floor(
            (offer.shares_offered * vestingMonths) / vestingSchedule.duration_months
          )
        }
        
        return {
          id: offer.id,
          user_id: offer.user_id,
          user_name: `${offer.users.first_name || ''} ${offer.users.last_name || ''}`.trim() || offer.users.email,
          shares_offered: offer.shares_offered,
          strike_price: offer.strike_price || 0,
          option_type: offer.option_type || 'NSO',
          grant_date: grantDate.toISOString().split('T')[0],
          vesting_start_date: grantDate.toISOString().split('T')[0],
          expiration_date: expirationDate.toISOString().split('T')[0],
          exercise_window_months: offer.exercise_window_months || 90,
          vested_shares: vestedShares,
          exercised_shares: 0, // Would come from option_exercises table
          status: offer.status,
          days_until_expiration: daysUntilExpiration
        }
      })
      
      setOptions(processedOptions)
      
      // Count expiring options (within 1 year)
      const expiring = processedOptions.filter((opt: OptionGrant) => 
        opt.days_until_expiration < 365 && opt.days_until_expiration > 0
      ).length
      setExpiringCount(expiring)
      
      // Calculate total vested but unexercised
      const totalVestedUnexercised = processedOptions.reduce((sum: number, opt: OptionGrant) => 
        sum + (opt.vested_shares - opt.exercised_shares), 0
      )
      setVestedUnexercised(totalVestedUnexercised)
    }
    
    setLoading(false)
  }

  const getExpirationBadge = (daysUntilExpiration: number) => {
    if (daysUntilExpiration < 0) {
      return <Badge variant="destructive">Expired</Badge>
    } else if (daysUntilExpiration < 90) {
      return <Badge variant="destructive">Expiring Soon</Badge>
    } else if (daysUntilExpiration < 365) {
      return <Badge variant="outline" className="border-yellow-600 text-yellow-600">Within 1 Year</Badge>
    }
    return null
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Option Tracking</h1>
        <p className="text-gray-600 mt-1">Monitor option grants, vesting, and expiration dates</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Options Granted</CardTitle>
            <TrendingDown className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {options.reduce((sum, opt) => sum + opt.shares_offered, 0).toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground">
              Across {options.length} grants
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Vested Unexercised</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{vestedUnexercised.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              Ready to exercise
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Expiring Soon</CardTitle>
            <AlertCircle className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">{expiringCount}</div>
            <p className="text-xs text-muted-foreground">
              Within next 12 months
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Options Table */}
      <Card>
        <CardHeader>
          <CardTitle>Active Option Grants</CardTitle>
          <CardDescription>
            All accepted option grants with vesting and expiration details
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Recipient</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Granted</TableHead>
                  <TableHead className="text-right">Vested</TableHead>
                  <TableHead className="text-right">Exercised</TableHead>
                  <TableHead className="text-right">Strike Price</TableHead>
                  <TableHead>Grant Date</TableHead>
                  <TableHead>Expiration</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {options.map((option) => (
                  <TableRow key={option.id}>
                    <TableCell className="font-medium">{option.user_name}</TableCell>
                    <TableCell>{option.option_type}</TableCell>
                    <TableCell className="text-right">{option.shares_offered.toLocaleString()}</TableCell>
                    <TableCell className="text-right">{option.vested_shares.toLocaleString()}</TableCell>
                    <TableCell className="text-right">{option.exercised_shares.toLocaleString()}</TableCell>
                    <TableCell className="text-right">{formatCurrency(option.strike_price)}</TableCell>
                    <TableCell>{option.grant_date}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-3 w-3 text-gray-500" />
                        {option.expiration_date}
                      </div>
                    </TableCell>
                    <TableCell>{getExpirationBadge(option.days_until_expiration)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}